import {Component, computed, ElementRef, signal, viewChild, ViewChild, WritableSignal} from '@angular/core';
import {LoadingMaskOptions} from 'frontend/src/app/classes/loading-mask-options';
import {NomChartConfigOptions} from 'frontend/src/shared/interfaces/nom-chart-config.interface';
import {BaseElement} from '../../../../classes/base-element';
import {ElementOptionsConfig} from '../../../../classes/element-option';
import {ExportComponents} from '../../../../../../enums/export-components.enum';
import {FileDownload} from '../../../../../../classes/file-download';

@Component({
    selector: 'eaglei-sit-rep-chart-element',
    templateUrl: './sit-rep-chart-element.component.html',
    styleUrls: ['../sit-rep-element.scss', './sit-rep-chart-element.component.scss'],
    standalone: false,
})
export class SitRepChartElementComponent extends BaseElement {
    public filters = this.sitRepService.sitRepFilters;

    // We need to track if its the first time the event loads to determine what start date we use
    private eventLoaded = false;
    // If we change the event we need to know so we can reset things to the default start date
    private previousEventId!: number;

    public chartStartDate = computed(() => {
        const filters = this.filters();
        // If its the first time the event is loaded or if we change an event, reset things
        if (this.previousEventId !== filters.event.id) {
            this.previousEventId = filters.event.id;
            this.eventLoaded = false;
        }
        // Default to showing the last 12 hours of an event
        if (!this.eventLoaded) {
            this.eventLoaded = true;
            return filters.endDate.clone().subtract(12, 'hours');
        }
        // Else show whatever start date the user selects
        return filters.startDate.clone();
    });

    public maskOptions: LoadingMaskOptions = new LoadingMaskOptions();
    public largeChart: WritableSignal<'large' | 'small'> = signal('large');
    public chartConfigOptions: NomChartConfigOptions = {
        type: 'bar',
    };
    public configureOptions: ElementOptionsConfig = {
        header: 'Chart Options',
        options: [
            {
                type: 'select',
                label: 'Chart Type',
                value: 'bar',
                selectOptions: [
                    {label: 'Bar', value: 'bar'},
                    {label: 'Line', value: 'line'},
                ],
            },
            {
                type: 'select',
                label: 'Y Axis Interval',
                value: 500000,
                selectOptions: [
                    {label: '10K', value: 10000},
                    {label: '50K', value: 50000},
                    {label: '100K', value: 100000},
                    {label: '500K', value: 500000},
                    {label: '1M', value: 1000000},
                    {label: '2M', value: 2000000},
                ],
            },
        ],
    };

    constructor() {
        super();
    }

    /**
     * Opens the Config Panel with the options of the element to change.
     * Waits for a response on what options to use.
     */
    protected configure(): void {
        this.sitRepService.openConfigSideBar(this as unknown as BaseElement, this.configureOptions);
    }

    public updateConfigValues(options: ElementOptionsConfig): void {
        // Handle updating the values from the config
        this.configureOptions = options;

        this.chartConfigOptions = {
            type: this.configureOptions.options.find((o) => o.label === 'Chart Type').value,
            yAxisInterval: this.configureOptions.options.find((o) => o.label === 'Y Axis Interval').value,
        };
    }

    exportComponent() {
        const config = {
            component: ExportComponents.NOM_CHART,
            height: 450,
            inputs: {
                startDate: this.chartStartDate(),
                endDate: this.filters().endDate.toISOString(),
                states: this.filters().locations.map((location) => location.abbreviation),
            },
        };

        this.sitRepService.exportComponent([config]).subscribe({
            next: (blobUrl: string) => {
                FileDownload.downloadFile(`situation-report-chart.png`, blobUrl);
            },
            error: (error) => {
                console.error('Unable to export table data', error);
            },
        });
    }
}
